import { VStack, StackProps, HStack, Icon, Tooltip, Box } from "@chakra-ui/react";
import { NewHireJourney } from "models/v2/newHireJourney";
import { longAttributeStyles } from "components/DataDisplay/styles";
import { FiAlertCircle } from "react-icons/fi";
import { useFeature } from "hooks/useFeature";
import EditSupportCrewMemberButton, {
  MANAGER_FIELD_SCHEMA,
  BUDDY_FIELD_SCHEMA,
  PEOPLE_TEAM_CONTACT_FIELD_SCHEMA,
  RECRUITER_FIELD_SCHEMA,
  IT_TEAM_CONTACT_FIELD_SCHEMA,
} from "./EditSupportCrewMemberButton";
import {
  HorizontalBuddyInfo,
  HorizontalManagerInfo,
  HorizontalPeopleTeamContactInfo,
  HorizontalRecruiterInfo,
  HorizontalItTeamContactInfo,
} from "./SupportTeamFields";

interface HorizontalNewHireSupportTeamProps extends StackProps {
  size?: "sm" | "md";
  newHireJourney: NewHireJourney;
}

function WarningIndicator({ show, label }: { show: boolean; label: string }) {
  if (!show) return null;
  return (
    <Tooltip label={`${label} not assigned`} placement="top">
      <Box display="inline-flex" alignItems="center">
        <Icon as={FiAlertCircle} color="error.DEFAULT" boxSize={4} ml={2} />
      </Box>
    </Tooltip>
  );
}

export default function HorizontalNewHireSupportTeam({
  newHireJourney,
  size = "md",
  ...rest
}: HorizontalNewHireSupportTeamProps) {
  const { enabled: extendedRolesEnabled } = useFeature("extended_journey_roles");
  const hasManager = !!newHireJourney.manager;
  const hasBuddy = !!newHireJourney.buddy;
  const hasPeopleTeamContact = !!newHireJourney.peopleTeamContact;
  const hasRecruiter = !!newHireJourney.recruiter;
  const hasItTeamContact = !!newHireJourney.itTeamContact;

  return (
    <VStack w="100%" align="stretch" gap="4" {...rest}>
      <HStack w="100%" justify="space-between">
        <HStack flex="1">
          <HorizontalManagerInfo
            newHireJourney={newHireJourney}
            size={size}
            labelContainerProps={longAttributeStyles.labelContainerProps}
          />
          <WarningIndicator show={!hasManager} label="Manager" />
        </HStack>
        <EditSupportCrewMemberButton
          newHireJourneyId={newHireJourney.id}
          formSchema={MANAGER_FIELD_SCHEMA}
          label="Manager"
        />
      </HStack>

      <HStack w="100%" justify="space-between">
        <HStack flex="1">
          <HorizontalBuddyInfo
            newHireJourney={newHireJourney}
            size={size}
            labelContainerProps={longAttributeStyles.labelContainerProps}
          />
          <WarningIndicator show={!hasBuddy} label="Buddy" />
        </HStack>
        <EditSupportCrewMemberButton
          newHireJourneyId={newHireJourney.id}
          formSchema={BUDDY_FIELD_SCHEMA}
          label="Buddy"
        />
      </HStack>

      <HStack w="100%" justify="space-between">
        <HStack flex="1">
          <HorizontalPeopleTeamContactInfo
            newHireJourney={newHireJourney}
            size={size}
            labelContainerProps={longAttributeStyles.labelContainerProps}
          />
          <WarningIndicator show={!hasPeopleTeamContact} label="People Team Contact" />
        </HStack>
        <EditSupportCrewMemberButton
          newHireJourneyId={newHireJourney.id}
          formSchema={PEOPLE_TEAM_CONTACT_FIELD_SCHEMA}
          label="People Team Contact"
        />
      </HStack>

      {extendedRolesEnabled && (
        <>
          <HStack w="100%" justify="space-between">
            <HStack flex="1">
              <HorizontalRecruiterInfo
                newHireJourney={newHireJourney}
                size={size}
                labelContainerProps={longAttributeStyles.labelContainerProps}
              />
              <WarningIndicator show={!hasRecruiter} label="Recruiter" />
            </HStack>
            <EditSupportCrewMemberButton
              newHireJourneyId={newHireJourney.id}
              formSchema={RECRUITER_FIELD_SCHEMA}
              label="Recruiter"
            />
          </HStack>

          <HStack w="100%" justify="space-between">
            <HStack flex="1">
              <HorizontalItTeamContactInfo
                newHireJourney={newHireJourney}
                size={size}
                labelContainerProps={longAttributeStyles.labelContainerProps}
              />
              <WarningIndicator show={!hasItTeamContact} label="IT Team Contact" />
            </HStack>
            <EditSupportCrewMemberButton
              newHireJourneyId={newHireJourney.id}
              formSchema={IT_TEAM_CONTACT_FIELD_SCHEMA}
              label="IT Team Contact"
            />
          </HStack>
        </>
      )}
    </VStack>
  );
}
